/**
 * Table Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { QuantityInput } from '../../components/form/MaskedInputs';

const tableSchema = z.object({
  number: z.number().int().min(1, 'Número deve ser maior que 0'),
  capacity: z.number().int().min(1, 'Capacidade deve ser maior que 0'),
  status: z.string().min(1, 'Status é obrigatório'),
  is_active: z.boolean().optional(),
});

export function TableModal({ open, onOpenChange, table, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!table?.id;

  const { handleSubmit, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(tableSchema),
    defaultValues: {
      number: 1,
      capacity: 4,
      status: 'disponivel',
      is_active: true,
    },
  });

  // Reset form when modal opens or table changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (table) {
      reset({
        number: table.number || 1,
        capacity: table.capacity || 4,
        status: table.status || 'disponivel',
        is_active: table.is_active ?? true,
      });
    } else {
      reset({
        number: 1,
        capacity: 4,
        status: 'disponivel',
        is_active: true,
      });
    }
  }, [open, table, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('tables.editTable') : t('tables.newTable')}
      description={isEditing ? 'Atualize os dados da mesa' : 'Preencha os dados da nova mesa'}
      size="sm"
      formId="table"
      entityId={table?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="number">{t('tables.number')} *</Label>
            <Controller
              name="number"
              control={control}
              render={({ field }) => (
                <QuantityInput
                  id="number"
                  value={field.value}
                  onValueChange={field.onChange}
                  min={1}
                />
              )}
            />
            {errors.number && <p className="text-sm text-destructive">{errors.number.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">{t('tables.capacity')} *</Label>
            <Controller
              name="capacity"
              control={control}
              render={({ field }) => (
                <QuantityInput
                  id="capacity"
                  value={field.value}
                  onValueChange={field.onChange}
                  min={1}
                  max={20}
                />
              )}
            />
            {errors.capacity && <p className="text-sm text-destructive">{errors.capacity.message}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">{t('tables.status')}</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponivel">{t('tables.available')}</SelectItem>
                  <SelectItem value="ocupada">{t('tables.occupied')}</SelectItem>
                  <SelectItem value="reservada">{t('tables.reserved')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default TableModal;
